
import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  ExternalLink,
  Github,
  Code2,
  TrendingUp,
  Sparkles,
} from "lucide-react";

const projects = [
  {
    id: 1,
    title: "CAB Hire",
    category: "development",
    type: "Corporate Website",
    description:
      "Modern corporate website with advanced features, responsive design and SEO optimization.",
    image:
      "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=800",
    technologies: ["WordPress", "SEO"],
    link: "https://cabhire.in/",
    features: ["Responsive Design", "SEO Optimized", "Admin Dashboard"],
    status: "Live",
  },

  {
    id: 2,
    title: "UrbanNest Properties",
    category: "marketing",
    type: "Real Estate Company",
    description:
      "Complete real estate platform with property listings, lead generation and SEO-focused content.",
    image:
      "https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=800",
    technologies: ["React", "Firebase", "Google Maps API"],
    link: "https://urbannestprop.com/",
    features: ["Property Search", "Virtual Tours", "Lead Management"],
    status: "Live",
  },

  {
    id: 3,
    title: "Jewellery Show Website",
    category: "design",
    type: "Website",
    description:
      "Elegant website platform created for a luxury jewellery brand with a premium visual experience.",
    image:
      "https://images.unsplash.com/photo-1535632066927-ab7c9ab60908?auto=format&fit=crop&w=800",
    technologies: ["Next.js", "Shopify", "Tailwind"],
    link: "https://thejewelleryshow.in/",
    features: ["Product Catalog", "Shopping Cart", "Payment Gateway"],
    status: "Live",
  },

  {
    id: 4,
    title: "Suvarna Construction",
    category: "development",
    type: "Business Website",
    description:
      "Business website for a construction company featuring projects, services and lead-generation sections.",
    image:
      "https://images.unsplash.com/photo-1504307651254-35680f356dfd?auto=format&fit=crop&w=800",
    technologies: ["React", "Tailwind CSS", "HTML/CSS", "WordPress"],
    link: "#",
    features: ["Project Gallery", "Contact Forms", "Service Pages", "Landing Page"],
    status: "Live",
  },

  {
    id: 5,
    title: "Digital Marketing",
    category: "marketing",
    type: "Digital Marketing",
    description:
      "Digital marketing workflow using analytics, campaign tracking, content creation and AI tools.",
    image:
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=800",
    technologies: [
      "SEO",
      "Google Ads",
      "Analytics",
      "Content Creation",
      "ChatGPT",
    ],
    link: "https://www.semrush.com/home/",
    features: [
      "Campaign Tracking",
      "SEO Analysis",
      "Report Generation",
      "Content Creation",
    ],
    status: "USED",
  },

  {
    id: 6,
    title: "Portfolio Maker",
    category: "development",
    type: "Portfolio Website",
    description:
      "Personal portfolio project combining coding, content creation and modern responsive design.",
    image:
      "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?auto=format&fit=crop&w=800",
    technologies: ["Coding", "Non-coding", "Own Content"],
    link: "#",
    features: ["Responsive Design", "Project Showcase", "Personal Branding"],
    status: "Done",
  },

  // ================================
  // NEW ACUITY PROJECTS
  // ================================

  {
    id: 7,
    title: "Acuity Groups LLP",
    category: "development",
    type: "Integrated Facility Management",
    description:
      "Professional corporate website for an integrated facility management company serving commercial, residential and industrial properties in Bangalore.",
    image:
      "https://images.unsplash.com/photo-1497366754035-f200968a6e72?auto=format&fit=crop&w=800",
    technologies: [
      "React",
      "Tailwind CSS",
      "SEO",
      "Responsive Design",
      "Lead Generation",
    ],
    link: "https://www.acuitygroups.in/",
    features: [
      "Integrated Facility Management",
      "Service Pages",
      "SEO Optimization",
      "Lead Generation",
    ],
    status: "Live",
  },

  {
    id: 8,
    title: "Acuity Pest Controls",
    category: "development",
    type: "Pest Control Website",
    description:
      "Lead-focused pest control website designed for pest management services in Bangalore with SEO, service pages and enquiry generation.",
    image:
      "https://images.unsplash.com/photo-1581578731548-c64695cc6952?auto=format&fit=crop&w=800",
    technologies: [
      "React",
      "Tailwind CSS",
      "SEO",
      "Google Ads",
      "Lead Generation",
    ],
    link: "https://www.acuitypestcontrols.com/",
    features: [
      "Pest Control Services",
      "SEO Optimization",
      "Enquiry Form",
      "Lead Generation",
    ],
    status: "Live",
  },
];

const ProjectCard = ({ project, index }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="relative group"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="rounded-2xl overflow-hidden glass-effect border border-white/10 h-full">
        {/* Image */}
        <div className="relative h-48 overflow-hidden">
          <img
            src={project.image}
            alt={project.title}
            className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
          />

          {/* Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>

          {/* Status */}
          <div
            className={`absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-semibold ${
              project.status === "Live"
                ? "bg-green-500/20 text-green-400 border border-green-500/30"
                : "bg-yellow-500/20 text-yellow-400 border border-yellow-500/30"
            }`}
          >
            {project.status}
          </div>

          {/* Hover Actions */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: hovered ? 1 : 0 }}
            className="absolute inset-0 flex items-center justify-center space-x-4 bg-black/70"
          >
            {project.link && project.link !== "#" && (
              <motion.a
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                href={project.link}
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 rounded-full bg-white text-black hover:bg-gray-100"
                aria-label={`Visit ${project.title}`}
              >
                <ExternalLink size={20} />
              </motion.a>
            )}

            {project.github && (
              <motion.a
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                href={project.github}
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 rounded-full bg-gray-800 text-white hover:bg-gray-700"
                aria-label={`View ${project.title} GitHub repository`}
              >
                <Github size={20} />
              </motion.a>
            )}
          </motion.div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Category */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              {project.category === "development" && (
                <Code2 size={16} className="text-blue-400" />
              )}

              {project.category === "marketing" && (
                <TrendingUp size={16} className="text-purple-400" />
              )}

              {project.category === "design" && (
                <Sparkles size={16} className="text-pink-400" />
              )}

              <span className="text-sm text-gray-400">
                {project.type}
              </span>
            </div>
          </div>

          {/* Title */}
          <h3 className="text-xl font-bold mb-2">
            {project.title}
          </h3>

          {/* Description */}
          <p className="text-gray-400 mb-4">
            {project.description}
          </p>

          {/* Technologies */}
          <div className="flex flex-wrap gap-2 mb-4">
            {project.technologies.map((tech, i) => (
              <span
                key={i}
                className="px-3 py-1 text-xs rounded-full bg-white/10 text-gray-300"
              >
                {tech}
              </span>
            ))}
          </div>

          {/* Features */}
          <div className="space-y-2">
            {project.features.map((feature, i) => (
              <div
                key={i}
                className="flex items-center space-x-2 text-sm text-gray-400"
              >
                <div className="w-1.5 h-1.5 rounded-full bg-primary-500"></div>

                <span>{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Floating Elements */}
      <div className="absolute -inset-4 bg-gradient-to-r from-primary-500/10 to-secondary-500/10 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10"></div>
    </motion.div>
  );
};

const Projects = () => {
  const [filter, setFilter] = useState("all");
  const [viewMode, setViewMode] = useState("grid");

  const filteredProjects =
    filter === "all"
      ? projects
      : projects.filter((p) => p.category === filter);

  return (
    <section id="projects" className="py-20 relative">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-grid-white/5 bg-grid-16"></div>

      {/* Animated Orbs */}
      <div className="absolute top-1/3 left-1/4 w-64 h-64 bg-primary-500/10 rounded-full blur-3xl animate-pulse-slow"></div>

      <div className="absolute bottom-1/3 right-1/4 w-64 h-64 bg-secondary-500/10 rounded-full blur-3xl animate-pulse-slow delay-1000"></div>

      <div className="container mx-auto px-4 max-w-7xl relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full glass-effect border border-white/10 mb-4">
            <Code2 className="w-4 h-4 text-primary-400" />

            <span className="text-sm">Portfolio</span>
          </div>

          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Featured
            <span className="block gradient-text">
              Projects
            </span>
          </h2>

          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            A showcase of my recent work in website development,
            design, SEO, and digital marketing.
          </p>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-12"
        >
          <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
            {/* Filter Buttons */}
            <div className="flex flex-wrap gap-2">
              {[
                "all",
                "development",
                "marketing",
                "design",
              ].map((cat) => (
                <button
                  key={cat}
                  onClick={() => setFilter(cat)}
                  className={`px-4 py-2 rounded-full transition-all ${
                    filter === cat
                      ? "bg-gradient-to-r from-primary-500 to-secondary-500 text-white"
                      : "glass-effect text-gray-300 hover:bg-white/10"
                  }`}
                >
                  {cat === "all"
                    ? "All Projects"
                    : cat.charAt(0).toUpperCase() + cat.slice(1)}
                </button>
              ))}
            </div>

            {/* View Toggle */}
            <div className="flex items-center space-x-2 glass-effect rounded-full p-1">
              <button
                onClick={() => setViewMode("grid")}
                className={`p-2 rounded-full ${
                  viewMode === "grid" ? "bg-white/10" : ""
                }`}
                aria-label="Grid view"
              >
                <div className="w-4 h-4 grid grid-cols-2 gap-0.5">
                  {[...Array(4)].map((_, i) => (
                    <div
                      key={i}
                      className="bg-gray-400 rounded-sm"
                    ></div>
                  ))}
                </div>
              </button>

              <button
                onClick={() => setViewMode("masonry")}
                className={`p-2 rounded-full ${
                  viewMode === "masonry" ? "bg-white/10" : ""
                }`}
                aria-label="Masonry view"
              >
                <div className="w-4 h-4 flex flex-col justify-between">
                  <div className="h-1 bg-gray-400 rounded-full"></div>
                  <div className="h-1 bg-gray-400 rounded-full"></div>
                  <div className="h-1 bg-gray-400 rounded-full"></div>
                </div>
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              {
                value: projects.length,
                label: "Total Projects",
              },
              {
                value: "100%",
                label: "Client Satisfaction",
              },
              {
                value: "20+",
                label: "Technologies Used",
              },
              {
                value: "4+",
                label: "Years Experience",
              },
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="p-4 rounded-2xl glass-effect border border-white/10 text-center"
              >
                <div className="text-2xl font-bold gradient-text">
                  {stat.value}
                </div>

                <div className="text-sm text-gray-400 mt-1">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Projects Grid */}
        <div
          className={`grid ${
            viewMode === "grid"
              ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
              : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
          } gap-8`}
        >
          {filteredProjects.map((project, index) => (
            <div
              key={project.id}
              className={
                viewMode === "masonry" && index % 3 === 1
                  ? "md:mt-8"
                  : ""
              }
            >
              <ProjectCard
                project={project}
                index={index}
              />
            </div>
          ))}
        </div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 text-center"
        >
          <div className="inline-block p-8 rounded-3xl glass-effect border border-white/10">
            <h3 className="text-2xl font-bold mb-4">
              Have a project in mind?
            </h3>

            <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
              Let's collaborate to create something amazing together.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Projects;


